/**
 * directorsService — Client for the Directors multi-agent prompt agent.
 *
 * Two operations:
 *   - `validate(guion, previousAnswers?, sessionId?)` → AD gatekeeper JSON.
 *   - `executeStream({ guionEnriquecido, nodeType, sessionId }, onEvent)`
 *      → opens a POST that streams Server-Sent Events. `onEvent(event, data)`
 *      is called for each parsed event: pipeline_started, agent_started,
 *      agent_completed, master_prompt, error, done. Returns an AbortController
 *      so the caller can cancel the pipeline.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth token getter, wired by AuthSetup at mount time (same pattern as
// projectService — the raw fetch() below can't reuse axios interceptors,
// so we resolve the JWT explicitly right before the streaming POST).
let getAuthToken = null;
export const setDirectorsAuthTokenGetter = (getter) => {
  getAuthToken = getter;
};

export const directorsService = {
  async validate({ guion, previousAnswers, sessionId }) {
    const res = await axios.post(`${API_URL}/api/directors/validate`, {
      guion,
      previous_answers: previousAnswers,
      session_id: sessionId,
    });
    return res.data;
  },

  async getCost() {
    const res = await axios.get(`${API_URL}/api/directors/cost`);
    return res.data;
  },

  /**
   * Stream the pipeline. Parses one SSE frame at a time from a raw ReadableStream.
   * `onEvent(event, data)` receives typed JSON.
   * Returns { abort } — call abort() to cancel mid-pipeline.
   */
  executeStream({ guionEnriquecido, nodeType, sessionId }, onEvent) {
    const controller = new AbortController();

    (async () => {
      let bearer = null;
      try {
        if (typeof getAuthToken === 'function') {
          const token = await getAuthToken();
          if (token) bearer = `Bearer ${token}`;
        }
      } catch { /* fall through — request will 401 which we surface below */ }

      const headers = { 'Content-Type': 'application/json' };
      if (bearer) headers.Authorization = bearer;

      try {
        const res = await fetch(`${API_URL}/api/directors/execute`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            guion_enriquecido: guionEnriquecido,
            node_type: nodeType,
            session_id: sessionId,
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          onEvent('error', { message: `HTTP ${res.status} ${res.statusText}` });
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        // Parse SSE frames: `event: X\ndata: {...}\n\n`
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            const frame = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let eventName = 'message';
            let dataLine = '';
            for (const line of frame.split('\n')) {
              if (line.startsWith('event:')) eventName = line.slice(6).trim();
              else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
            }
            if (dataLine) {
              try {
                onEvent(eventName, JSON.parse(dataLine));
              } catch (e) {
                onEvent('error', { message: `bad frame: ${e.message}` });
              }
            }
          }
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          onEvent('error', { message: err?.message || 'stream failed' });
        }
      }
    })();

    return { abort: () => controller.abort() };
  },
};

export default directorsService;
